import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import { Modal, Button } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import TablePagination from "./TablePagination";
import { getCareers, deleteCareer } from "../Apis/CareerAPIs";
import usePermissions from "../hook/userPermission";

const CareerLayer = () => {
  const { hasPermission } = usePermissions();
  const navigate = useNavigate();
  const [careers, setCareers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");

  // Modal states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const fetchCareers = async () => {
    setLoading(true);
    const res = await getCareers(debouncedSearchTerm);
    if (res.status) {
      const data = res.response?.data || res.response;
      setCareers(Array.isArray(data) ? data : []);
    }
    setLoading(false);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  useEffect(() => {
    fetchCareers();
  }, [debouncedSearchTerm]);

  const handleDeleteClick = (career) => {
    setItemToDelete(career);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!itemToDelete) return;
    setIsUpdating(true);
    const res = await deleteCareer(itemToDelete._id || itemToDelete.id);
    if (res.status) {
      toast.success("Career Opening Deleted Successfully");
      fetchCareers();
      setShowDeleteModal(false);
      setItemToDelete(null);
    }
    setIsUpdating(false);
  };

  const paginatedCareers = careers.slice(
    currentPage * rowsPerPage,
    (currentPage + 1) * rowsPerPage
  );

  const totalPages = Math.ceil(careers.length / rowsPerPage);

  const formatPrice = (val) => {
    if (!val) return "N/A";
    const cleanValue = val.toString().replace(/,/g, "");
    return cleanValue.replace(/\d+/g, (n) => {
        return n.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    });
  };

  return (
    <div className="row gy-4">
      <div className="col-12">
        <div className="card h-100 p-0 radius-12 shadow-sm">
          <div
            className="card-header border-bottom py-12 px-24 d-flex align-items-center justify-content-between"
            style={{
              background: "linear-gradient(135deg, #000000ff, #bb0007ff, #ff0000ff)",
            }}
          >
            <h6 className="text-white mb-0">Career Management</h6>
            <div className="d-flex align-items-center gap-3">
              <div className="navbar-search position-relative">
                <input
                  type="text"
                  className="form-control radius-8 ps-40 py-10 bg-white border-0"
                  style={{ height: "44px" }}
                  placeholder="Search job roles..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(0);
                  }}
                />
                <Icon
                  icon="solar:magnifer-linear"
                  className="position-absolute text-secondary-light"
                  style={{ left: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "18px" }}
                />
              </div>
              {hasPermission("careers", "add") && (
                <button
                  onClick={() => navigate("/careers/add")}
                  className="btn btn-sm radius-8 px-16 py-10 d-flex align-items-center gap-2"
                  style={{ backgroundColor: "#f7e7da", color: "black", fontWeight: "600" }}
                >
                  <Icon icon="ic:baseline-plus" className="text-xl" />
                  Add Career
                </button>
              )}
            </div>
          </div>
          
          <div className="card-body">
            <div className="table-responsive scroll-sm">
              <table className="table bordered-table sm-table mb-0">
                <thead style={{ backgroundColor: "#f7e7da" }}>
                  <tr>
                    <th scope="col" style={{ color: "black", fontWeight: "bold" }}>S.No</th>
                    <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Job Role</th>
                    <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Display Order</th>
                    <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Type</th>
                    <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Mode</th>
                    <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Location</th>
                    <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Experience</th>
                    <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Openings</th>
                    <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Status</th>
                    <th scope="col" style={{ color: "black", fontWeight: "bold" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan="10" className="text-center py-24">Loading...</td></tr>
                  ) : paginatedCareers.length > 0 ? (
                    paginatedCareers.map((career, index) => (
                      <tr key={career._id || career.id}>
                        <td>{currentPage * rowsPerPage + index + 1}</td>
                        <td><span>{career.jobRole}</span></td>
                        <td><span>{career.displayOrder ?? 0}</span></td>
                        <td>{career.employmentType}</td>
                        <td>{career.workMode}</td>
                        <td>{career.location}</td>
                        <td>{career.experience}</td>
                        <td><span className="badge bg-info-focus text-info-600">{career.openings}</span></td>
                        <td>
                          <span className={`badge ${career.status === 'Active' ? 'bg-success' : 'bg-danger'} radius-4`}>
                            {career.status}
                          </span>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            {hasPermission("careers", "view") && (
                              <button
                                onClick={() => navigate(`/careers/enquiries/${career._id || career.id}`)}
                                className="bg-info-focus text-info-600 bg-hover-info-200 w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                title="View Enquiries"
                              >
                                <Icon icon="solar:users-group-two-rounded-bold" className="text-lg" />
                              </button>
                            )}
                            {hasPermission("careers", "edit") && (
                              <button
                                onClick={() => navigate(`/careers/edit/${career._id || career.id}`)}
                                className="bg-success-focus text-success-600 bg-hover-success-200 w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                title="Edit"
                              >
                                <Icon icon="lucide:edit" className="text-lg" />
                              </button>
                            )}
                            {hasPermission("careers", "delete") && (
                              <button
                                onClick={() => handleDeleteClick(career)}
                                className="bg-danger-focus text-danger-600 bg-hover-danger-200 w-32-px h-32-px d-flex justify-content-center align-items-center rounded-circle border-0"
                                title="Delete"
                              >
                                <Icon icon="lucide:trash-2" className="text-lg" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr><td colSpan="10" className="text-center py-24 text-secondary-light">No career openings found</td></tr>
                  )}
                </tbody>
              </table>
            </div>
            <TablePagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value));
                setCurrentPage(0);
              }}
              totalRecords={careers.length}
            />
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Body className="text-center p-5">
          <div className="d-flex justify-content-center mb-3">
            <div className="bg-danger-focus rounded-circle d-flex justify-content-center align-items-center w-64-px h-64-px">
              <Icon icon="solar:trash-bin-trash-bold" className="text-danger-600 text-xxl" />
            </div>
          </div>
          <h5 className="mb-2">Are you sure?</h5>
          <p className="text-secondary-light mb-4">
            Do you want to delete career opening for "{itemToDelete?.jobRole}"?
          </p>
          <div className="d-flex justify-content-center gap-3 mt-4">
            <Button variant="outline-secondary" className="px-32 radius-8" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
            <Button className="px-32 radius-8 border-0" onClick={confirmDelete} disabled={isUpdating} style={{ backgroundColor: "#ed1c24", color: "white" }}>
              {isUpdating ? "Deleting..." : "Confirm"}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default CareerLayer;
